"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { X } from "lucide-react";
import {
  CheckoutOrchestrator,
  shouldGateCheckout,
  type BasketChoiceNotice,
  type CheckoutApiConfig,
  type ExistingParticipant,
} from "@baluu/ui/components/checkout";
import { fetchExistingParticipants } from "@baluu/ui/lib/checkout";
import { CHECKOUT_FRAME_CLASSES } from "@baluu/ui/lib/checkout/frame";
import { TermsDialogPortal } from "../TermsDialogPortal";
import { CustomerAuthDialog } from "../CustomerAuthDialog/CustomerAuthDialog";
import type { TermsSettings, CustomTerms } from "@baluu/ui/components/checkout/utils/types";
import type { SuccessData } from "../PortalManager";
import { useAuthSnapshot, hydrateSession } from "~/widgets/lib/auth-client";
import { describeErrorForLog } from "~/widgets/lib/utils";

// Default API URL
const DEFAULT_API_URL = "https://api.baluu.io";

/**
 * Payment data needed to render Stripe Elements in a portal
 */
export type StripePaymentData = {
  clientSecret: string;
  schoolAccountID?: string;
  paymentMethodType: string;
  purchaseId?: string;
  successUrl?: string;
  buyerEmail?: string;
  total?: number;
  currency?: string;
  courseName?: string;
  stripePublicKey?: string;
  grossTotal?: number;
  netTotal?: number;
  discountAmount?: number;
  discountType?: string;
};

export type CheckoutFlowProps = {
  listingId: string;
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  bookingType?: string;
  onClose: () => void;
  onComplete?: (data?: SuccessData) => void;
  /** Widget base URL for 3DS redirect return (e.g., "https://botanicalboys.live.baluu.co.uk") */
  /** Unused since the in-flow 3DS payment step was retired; kept for caller compatibility. */
  widgetBaseUrl?: string;
  /**
   * Callback when user wants to join the waiting list (no booking options available).
   * Receives the id of the sold-out session being joined (the orchestrator passes
   * the clicked session). When called, the parent shows the waiting list form.
   */
  onJoinWaitingList?: (sessionId?: string, eventId?: string) => void;
  /**
   * Pre-select a specific session on mount (skips date selection).
   * Used for timetable "Book Now" flow.
   */
  initialSessionId?: string;
  /**
   * Pre-select a booking type on mount.
   * Used together with initialSessionId for timetable flow.
   */
  initialBookingType?: string;
  /** A waiting-list offer being claimed (its hold id): the dates don't show
   *  the claimer's own held seats as taken (CheckoutOrchestrator `offerRef`). */
  offerRef?: string;
  // Basket / multi-date props
  isMultiDateEnabled?: boolean;
  maxDateSelections?: number;
  basketItemCount?: number;
  /** Why the overlay refused the last basket choice, shown on the choice
   *  step with how to fix it (see useBasketConflict). */
  basketConflict?: BasketChoiceNotice | null;
  /** The flow moved to another step (its id, e.g. "basketChoice"). */
  onStepChange?: (step: string) => void;
  /** Seats the visitor already holds in their basket, by session: the date
   *  picker takes them off "spots left". */
  reservedSessionQuantities?: Record<string, number>;
  onAddToBasket?: (itemData: any) => void;
  /** Required: CheckoutOrchestrator's matching prop is required — every flow
   *  now ends at the basket, so there is no in-flow fallback to fall back to. */
  onProceedToBasketCheckout: (itemData: any) => void;
  /** When true, skips the "Add to basket vs Checkout" UI and auto-proceeds to basket checkout */
  directCheckout?: boolean;
  /**
   * School slug for the customer sign-in dialog (OTP email branding, allowed-origin
   * lookup). Captured by the parent `<CheckoutOverlay>` from the with-bundles
   * response it already fetches — `undefined` until that response lands.
   */
  schoolSlug?: string;
  /**
   * School display name, woven into the sign-in dialog's copy ("Sign in to
   * {schoolName}."). Captured from the same with-bundles response as
   * `schoolSlug`; empty/undefined until it lands, which the dialog handles by
   * falling back to its generic phrasing rather than rendering a gap.
   */
  schoolName?: string;
  /**
   * Whether this school requires customers to sign in before booking. Also
   * captured from the parent's with-bundles response; `undefined` until that
   * response lands is treated as "don't gate yet" (same as `false`).
   */
  requireLoginToBook?: boolean;
};

/**
 * CheckoutFlow - Webcomponents checkout flow component
 *
 * This component wraps the shared CheckoutOrchestrator from @baluu/ui
 * with webcomponents-specific functionality:
 * - Shadow DOM provides CSS isolation
 * - When reaching payment step in inline receipt mode, triggers portal callback
 * - The parent web component (<baluu-checkout-overlay>) handles overlay positioning
 * - Webcomponents source tracking for analytics
 */
export function CheckoutFlow({
  listingId,
  businessId,
  apiUrl,
  primaryColor = "#03c2b9",
  onClose,
  onComplete,
  widgetBaseUrl,
  onJoinWaitingList,
  initialSessionId,
  initialBookingType,
  offerRef,
  isMultiDateEnabled = false,
  maxDateSelections = 10,
  basketItemCount = 0,
  basketConflict = null,
  onStepChange,
  reservedSessionQuantities,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  schoolSlug,
  schoolName,
  requireLoginToBook,
}: CheckoutFlowProps) {
  // Capture free booking result data from analytics events so we can
  // forward it through onComplete (the orchestrator's onSuccess is void).
  const freeBookingDataRef = useRef<Partial<SuccessData> | null>(null);
  const pricingDataRef = useRef<Record<string, unknown> | null>(null);

  // Build API config for the shared orchestrator
  // Note: source must be "widget" to enable commission system (isEligibleForNewSystem)
  // isWebcomponent flag ensures sourceInfo.sourceType is set to "webcomponent"
  const apiConfig: CheckoutApiConfig = {
    baseUrl: apiUrl || DEFAULT_API_URL,
    source: "widget",
    isWebcomponent: true,
    onAnalytics: (event, data) => {
      if (event === "pricing_summary" && data && typeof data === "object") {
        pricingDataRef.current = data as Record<string, unknown>;
      }

      if (event === "free_booking_confirmed" && data && typeof data === "object") {
        const d = data as Record<string, unknown>;
        freeBookingDataRef.current = {
          purchaseId: d.purchaseID as string | undefined,
          total: 0,
        };
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("baluu:analytics", {
            detail: { event, data },
          }),
        );
      }
    },
    onError: (error, context) => {
      // Message + HTTP status ONLY — never the raw error. See
      // `describeErrorForLog`: this runs on a partner's page, and the error
      // reaching here from `fetchExistingParticipants` is a raw AxiosError
      // whose `.config.headers.Authorization` still holds the customer's live
      // bearer token. `context` is the shared checkout lib's own breadcrumb
      // (`{ action, schoolId }`) and carries no credentials, so it stays.
      console.error(
        "[webcomponents] Checkout error:",
        describeErrorForLog(error),
        context,
      );
    },
  };

  // Customer session (auth-store). Validates any stored token once per page.
  const auth = useAuthSnapshot();
  const isLoggedIn = auth.status === "authenticated";
  const [authOpen, setAuthOpen] = useState(false);
  const openLogin = useCallback(() => setAuthOpen(true), []);
  const [existingParticipants, setExistingParticipants] = useState<ExistingParticipant[]>([]);

  /**
   * Whether the orchestrator is currently showing `CheckoutLoginGate` in place
   * of the checkout flow. Computed with the SAME `shouldGateCheckout` predicate
   * the orchestrator calls internally, from the SAME three values we hand it
   * below (`requireLogin` / `isLoggedIn` / `onLogin`) — if this were a hand-
   * rolled copy of the rule the two could drift, and the header would be
   * withheld in exactly the state that needs it.
   *
   * The gate only replaces the flow when a checkout is active
   * (`activeCheckout` truthy in the orchestrator), so `renderHeader` below ANDs
   * this with its own `activeCheckout` argument rather than reading it alone.
   */
  const isLoginGated = shouldGateCheckout({
    requireLogin: requireLoginToBook ?? false,
    isLoggedIn,
    onLogin: openLogin,
  });

  useEffect(() => {
    void hydrateSession(apiConfig.baseUrl);
  }, [apiConfig.baseUrl]);

  // Saved attendees for the signed-in customer (session-gated). Forwarded to
  // the orchestrator → participants step.
  useEffect(() => {
    if (!isLoggedIn || !businessId) {
      setExistingParticipants([]);
      return;
    }
    let cancelled = false;
    // Bearer header, NO credentials mode (/widget/* serves CORS "*"). Fail-open: [].
    fetchExistingParticipants(businessId, auth.token, apiConfig).then((list) => {
      if (!cancelled) setExistingParticipants(list);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, businessId, auth.token]);

  // Handle success - call onComplete callback with any captured data
  const handleSuccess = () => {
    const pricing = pricingDataRef.current;
    const baseData = freeBookingDataRef.current ?? {};
    const successData: Partial<SuccessData> = {
      ...baseData,
      ...(pricing && {
        grossTotal: pricing.grossTotal as number | undefined,
        netTotal: pricing.netTotal as number | undefined,
        discountAmount: pricing.discountAmount as number | undefined,
        discountType: pricing.discountType as string | undefined,
      }),
    };
    onComplete?.(Object.keys(successData).length > 0 ? successData : undefined);
    freeBookingDataRef.current = null;
    pricingDataRef.current = null;
  };

  // Handle error. Same redaction rule as apiConfig.onError above: the
  // orchestrator forwards whatever a flow threw, so this can be an AxiosError
  // too — log the message, never the object.
  const handleError = (error: Error | string) => {
    console.error("[webcomponents] Checkout error:", describeErrorForLog(error));
  };

  // Custom header for webcomponents:
  // - When viewing booking options (no active checkout): show close button
  // - When in a checkout flow (active checkout): hide header (checkout panel has its own close button)
  // - EXCEPT when the login gate has replaced that flow: the gate has no close
  //   button of its own, so the header is the only way out (see below)
  const renderHeader = ({
    activeCheckout,
    onClose: handleCloseHeader,
  }: {
    activeCheckout: unknown;
    onBack: () => void;
    onClose: () => void;
  }) => {
    // When a checkout is active, don't show header — the checkout panel has its
    // own close button.
    //
    // ...unless the orchestrator has swapped `CheckoutLoginGate` in for that
    // panel. `CheckoutLoginGate`'s contract is that it blocks the flow without
    // trapping the visitor — "the overlay's close button still works" — and it
    // ships no close button of its own precisely because it expects the header
    // to stay. Returning null here on `activeCheckout` alone therefore left a
    // signed-out customer at a `requireLoginToBook` school staring at a
    // full-viewport sign-in wall with no exit: no X, no reachable backdrop
    // (`.checkout-overlay-content` is `position: fixed; inset: 0` and the
    // orchestrator's own `w-full min-h-screen` div covers every pixel of it),
    // and no Escape handler anywhere in this chain. The orchestrator's default
    // header renders its close X regardless of `activeCheckout`, and the sibling
    // `BasketCheckoutOverlay` keeps its header outside the gate conditional for
    // the same reason; this matches both.
    if (activeCheckout && !isLoginGated) return null;

    // When showing booking options — or the login gate — show a minimal header
    // with a close button. `handleCloseHeader` is the orchestrator's `onClose`,
    // i.e. this component's `onClose` prop, which unmounts the whole overlay
    // (`<baluu-checkout-overlay>` is removed from the host page) rather than
    // merely stepping back a level, so it is a guaranteed exit from the gate.
    return (
      <header className={`sticky top-0 z-50 border-b ${CHECKOUT_FRAME_CLASSES.header}`}>
        <div className="container mx-auto px-1 sm:px-4 py-3 flex items-center justify-end">
          <button
            onClick={handleCloseHeader}
            className={`p-2 rounded-full transition-colors ${CHECKOUT_FRAME_CLASSES.close}`}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </header>
    );
  };

  // Ref for scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to top when step changes, and tell the overlay which step it is.
  const handleStepChange = useCallback(
    (step: string) => {
      onStepChange?.(step);
      setTimeout(() => {
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);
    },
    [onStepChange],
  );

  // Terms dialog state for Shadow DOM portal
  const [termsDialogData, setTermsDialogData] = useState<{
    isOpen: boolean;
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
    ticketPolicies?: any[];
  }>({ isOpen: false });

  // Handle opening terms dialog in Shadow DOM portal
  const handleOpenTermsDialog = useCallback((data: {
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
    ticketPolicies?: any[];
  }) => {
    setTermsDialogData({
      isOpen: true,
      ...data,
    });
  }, []);

  // Handle closing terms dialog
  const handleCloseTermsDialog = useCallback(() => {
    setTermsDialogData(prev => ({ ...prev, isOpen: false }));
  }, []);

  // The checkout content - fixed fullscreen overlay that escapes parent container constraints
  return (
    <div
      ref={scrollContainerRef}
      className="checkout-overlay-content flex items-start justify-center overflow-y-auto bg-black/50"
      data-checkout-content
    >
      <CheckoutOrchestrator
        listingId={listingId}
        apiConfig={apiConfig}
        // No primaryColor: the checkout draws in the host's variables (the
        // page's theme, or the `primary-color` attribute written to them
        // by withHostBrand), not a copy written inline over them.
        onClose={onClose}
        onSuccess={handleSuccess}
        onError={handleError}
        // Enable inline receipt for webcomponent + direct charges
        // For destination charges, will still redirect even with this enabled
        allowInlineReceipt={true}
        // Custom header: show close button on options, hide during checkout
        renderHeader={renderHeader}
        // Close overlay entirely when clicking panel's X (no options to go back to)
        closeOverlayOnPanelDismiss={true}
        // Scroll to top when step changes
        onStepChange={handleStepChange}
        // Join waiting list when no booking options available
        onJoinWaitingList={onJoinWaitingList}
        // Pre-selected session and booking type for timetable "Book Now" flow
        initialSessionId={initialSessionId}
        initialBookingType={initialBookingType as Parameters<typeof CheckoutOrchestrator>[0]["initialBookingType"]}
        offerRef={offerRef}
        // Open terms dialog in Shadow DOM portal (for proper styling in webcomponents)
        onOpenTermsDialog={handleOpenTermsDialog}
        // Basket / multi-date props
        isMultiDateEnabled={isMultiDateEnabled}
        maxDateSelections={maxDateSelections}
        basketItemCount={basketItemCount}
        basketConflict={basketConflict}
        reservedSessionQuantities={reservedSessionQuantities}
        onAddToBasket={onAddToBasket}
        onProceedToBasketCheckout={onProceedToBasketCheckout}
        directCheckout={directCheckout}
        // Customer session (auth-store). Gate fails open without onLogin — we always pass it.
        // These three are the exact inputs `isLoginGated` above is computed
        // from, so the header can never disagree with the orchestrator about
        // whether the gate is on screen.
        isLoggedIn={isLoggedIn}
        onLogin={openLogin}
        requireLogin={requireLoginToBook ?? false}
        existingParticipants={existingParticipants}
      />

      {/* Terms dialog rendered in Shadow DOM portal for proper styling */}
      <TermsDialogPortal
        isOpen={termsDialogData.isOpen}
        onClose={handleCloseTermsDialog}
        settings={termsDialogData.termsSettings}
        customTerms={termsDialogData.customTerms}
        primaryColor={primaryColor}
        ticketPolicies={termsDialogData.ticketPolicies}
      />

      {/* Customer sign-in dialog, rendered in Shadow DOM portal for proper styling */}
      <CustomerAuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        apiUrl={apiConfig.baseUrl}
        businessSlug={schoolSlug}
        schoolName={schoolName}
        primaryColor={primaryColor}
      />
    </div>
  );
}
